import React from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, useMediaQuery } from '@mui/material';
import toast, { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from 'react-error-boundary';

import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { BrandingProvider, useBranding } from './contexts/ThemeContext';

import AppRoutes from './components/routing/AppRoutes';
import ScrollToTop from './components/routing/ScrollToTop';
import ErrorFallback from './components/common/ErrorFallback';
import NotificationPermissionPrompt from './components/notifications/NotificationPermissionPrompt';
import InstallPrompt from './components/common/InstallPrompt';
import CookieConsentBanner from './components/legal/CookieConsentBanner';

import './styles/global.css';

// A new version is running in the service worker. Reloading on the spot threw
// away whatever the person was in the middle of — a half-written post, a quiz
// — so it is offered instead, and applied by itself the next time the app is
// in the background (switched away from, phone locked).
function offerUpdate() {
  const reload = () => window.location.reload();
  if (document.visibilityState === 'hidden') { reload(); return; }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') reload();
  }, { once: true });
  toast(
    (t) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        A new version of MoreMe is ready.
        <button
          type="button"
          onClick={() => { toast.dismiss(t.id); reload(); }}
          style={{ background: 'none', border: 0, color: 'inherit', font: 'inherit', fontWeight: 700, textDecoration: 'underline', padding: '8px 4px', cursor: 'pointer' }}
        >
          Refresh
        </button>
      </span>
    ),
    { id: 'app-update', duration: Infinity },
  );
}

// Register service worker for PWA + auto-refresh on new version.
// Important for the home-screen icon to pick up the new brand mark on
// Android Chrome: Chrome re-reads manifest.json when the SW activates a new
// version, so bumping CACHE_VERSION in /sw.js forces a manifest re-fetch +
// icon cache invalidation. iOS Safari caches icons at Add-to-Home-Screen
// time and requires a re-add — nothing we can do app-side.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // The very first visit also fires `controllerchange` (the new worker
    // claims the page). That isn't an update and used to reload the sign-in
    // page while people were typing, so only a page that already had a
    // worker treats it as one.
    const hadController = Boolean(navigator.serviceWorker.controller);
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        // Poll for updates every 60 min so users on long-running PWA sessions
        // pick up new worker versions without a full tab reload.
        setInterval(() => { registration.update().catch(() => {}); }, 60 * 60 * 1000);

        // `announced` guards against offering twice if several events fire.
        let announced = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!hadController || announced) return;
          announced = true;
          offerUpdate();
        });
      })
      .catch((error) => { console.log('SW registration failed:', error); });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function ThemedApp() {
  const { theme, branding } = useBranding();
  // Same breakpoint as the layout shell's fixed top bar.
  const isMobile = useMediaQuery('(max-width:899.95px)');

  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primaryColor);
    const pc = branding.primaryColor || '#2D5BE3';
    const r = parseInt(pc.slice(1,3),16), g = parseInt(pc.slice(3,5),16), b = parseInt(pc.slice(5,7),16);
    root.style.setProperty('--brand-primary-hover', `rgb(${Math.max(0,r-20)},${Math.max(0,g-20)},${Math.max(0,b-20)})`);
    root.style.setProperty('--brand-secondary', branding.secondaryColor);
    root.style.setProperty('--brand-accent', branding.accentColor);
    root.style.setProperty('--brand-sidebar', branding.sidebarColor);
    root.style.setProperty('--brand-sidebar-text', branding.sidebarTextColor);
    root.style.setProperty('--brand-header', branding.headerColor);
    root.style.setProperty('--brand-header-text', branding.headerTextColor);
  }, [branding]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <AuthProvider>
          <NotificationProvider>
            <SocketProvider>
              <div className="App">
                <AppRoutes />
                <NotificationPermissionPrompt />
                <InstallPrompt />
                <CookieConsentBanner />
                <Toaster
                  // On phones: centred, just under the fixed 56px top bar
                  // (and the notch) rather than over the bar's buttons.
                  position={isMobile ? 'top-center' : 'top-right'}
                  containerStyle={isMobile ? { top: 'calc(64px + env(safe-area-inset-top))' } : undefined}
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#1A1D1F',
                      color: '#fff',
                      borderRadius: '16px',
                      padding: '12px 16px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                      fontSize: '14px',
                      fontWeight: 500,
                    },
                    success: {
                      style: {
                        background: '#1A1D1F',
                        border: `1px solid ${branding.primaryColor}`,
                      },
                      iconTheme: {
                        primary: branding.primaryColor,
                        secondary: '#1A1D1F',
                      },
                    },
                    error: {
                      style: {
                        background: '#1A1D1F',
                        border: '1px solid #EF4444',
                      },
                      iconTheme: {
                        primary: '#EF4444',
                        secondary: '#1A1D1F',
                      },
                    },
                  }}
                />
              </div>
            </SocketProvider>
          </NotificationProvider>
        </AuthProvider>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary fallback={<ErrorFallback error={new Error('Something went wrong')} resetErrorBoundary={() => window.location.reload()} />}>
      <QueryClientProvider client={queryClient}>
        <BrandingProvider>
          <ThemedApp />
        </BrandingProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
